<template>
  <div class="following-page">
    <div class="page-heading">
      <div><h4>我的题材</h4><p>只提醒排名明显上升、持续状态变化和题材内容更新。</p></div>
      <b-button size="sm" variant="outline-primary" to="/wonder/topic/table">发现题材</b-button>
    </div>

    <b-alert v-if="error" show variant="warning">{{ error }}</b-alert>
    <div v-if="loading" class="text-center py-5"><b-spinner></b-spinner></div>
    <div v-else-if="!error && !items.length" class="empty-state">
      <b-icon icon="bookmark" font-scale="2"></b-icon><strong>还没有关注题材</strong>
      <span>从首页强势题材或题材详情点击“关注”，这里会持续跟踪变化。</span>
    </div>
    <div v-else class="follow-grid">
      <article v-for="item in items" :key="item.topic_id" class="follow-card">
        <header>
          <router-link :to="topicLink(item)">{{ cleanName(item.name) }}</router-link>
          <button @click="unfollow(item)">取消关注</button>
        </header>
        <div class="score-row">
          <span>强度 <b>{{ value(item.score) }}</b></span><span>排名 <b>{{ item.rank ? '#' + item.rank : '--' }}</b></span>
          <span :class="pctClass(item.today_pct)">均涨 <b>{{ pct(item.today_pct) }}</b></span>
        </div>
        <div v-if="item.continuity && item.continuity.available" class="state-box">
          <strong>{{ item.continuity.state }}</strong><span>持续 {{ value(item.continuity.continuity_score) }}</span>
          <p>{{ item.continuity.state_summary }}</p>
        </div>
        <div v-else class="state-box muted">历史评分不足，暂不形成持续判断。</div>
        <div class="card-footer-line">
          <span v-if="item.has_update" class="update-dot">有内容更新</span><span v-else>截至 {{ item.trade_date || '--' }}</span>
          <router-link :to="topicLink(item)">查看详情 →</router-link>
        </div>
        <div v-if="item.analysis_locked" class="unlock-line">
          完整五日轨迹、支持依据与风险条件为题材表格 VIP 权益
          <button @click="subscribe(item)">查看 ¥599 订阅</button>
        </div>
      </article>
    </div>

    <section v-if="alerts.length" class="alerts-section">
      <h5>最近变化 <b-badge v-if="unreadCount" variant="danger">{{ unreadCount }}</b-badge></h5>
      <div v-for="alert in alerts" :key="alert.id" :class="['alert-row', { unread: !alert.is_read }]">
        <span>{{ alert.trade_date }}</span><router-link :to="topicLink(alert)">{{ alert.title }}</router-link><p>{{ alert.summary }}</p>
      </div>
    </section>
  </div>
</template>

<script>
import { request_zzquant } from '@/network/request'
import { trackProductEvent } from '@/utils/productAnalytics'

export default {
  name: 'WonderTopicFollowing',
  data() { return { loading: true, error: '', items: [], alerts: [], unreadCount: 0 } },
  mounted() {
    if (!this.hasToken()) {
      this.$router.push({ path: '/login', query: { redirect: this.$route.fullPath } })
      return
    }
    this.load()
  },
  methods: {
    hasToken() { try { return Boolean(localStorage.getItem('token')) } catch (e) { return false } },
    cleanName(name) { return String(name || '').replace(/\s*[（(][^（）()]*[）)]/g, '').trim() },
    topicLink(item) { return { path: '/wonder/topic/detail/' + encodeURIComponent(item.unique_key || item.topic_id), query: { source: 'my_topics' } } },
    value(v) { return v === null || v === undefined ? '--' : Number(v).toFixed(1) },
    pct(v) { if (v === null || v === undefined) return '--'; const n = Number(v); return `${n > 0 ? '+' : ''}${n.toFixed(2)}%` },
    pctClass(v) { return Number(v) > 0 ? 'up' : Number(v) < 0 ? 'down' : '' },
    async load() {
      this.loading = true
      try {
        const [followRes, alertRes] = await Promise.all([
          request_zzquant({ url: '/v3/topic/follows', noRedirect: true }),
          request_zzquant({ url: '/v3/topic/follow-alerts', noRedirect: true })
        ])
        this.items = followRes.data.data.items || []
        this.unreadCount = followRes.data.data.unread_count || 0
        this.alerts = alertRes.data.data || []
        if (this.unreadCount) request_zzquant({ url: '/v3/topic/follow-alerts/read', method: 'POST', noRedirect: true }).catch(() => {})
      } catch (e) {
        const status = e && (e.status || (e.response && e.response.status))
        if (status === 401 || e.__unauthorized) {
          try { localStorage.removeItem('token') } catch (ignore) { /* ignore */ }
          this.$router.push({ path: '/login', query: { redirect: this.$route.fullPath } })
        } else {
          this.error = '关注题材加载失败，请稍后重试。'
        }
      } finally { this.loading = false }
    },
    async unfollow(item) { await request_zzquant({ url: `/v3/topic/follows/${item.topic_id}`, method: 'DELETE' }); this.items = this.items.filter(row => row.topic_id !== item.topic_id) },
    subscribe(item) {
      trackProductEvent('subscription_click', { topic_id: item.topic_id, source: 'my_topics', module_code: 'topic_kline' })
      this.$router.push({ path: '/subscription', query: { module: 'topic_kline', source: 'my_topics', topic_id: item.topic_id } })
    }
  }
}
</script>

<style scoped>
.following-page {
  --following-ink: var(--text-main, #263445);
  --following-muted: var(--text-muted, #718096);
  --following-border: var(--border-color, #dee5ec);
  --following-card-bg: var(--bg-card, #fff);
  --following-soft-bg: var(--bg-muted, #f5f7fa);
  --following-state-text: #536174;
  --following-up: #c84435;
  --following-down: #269c50;
  --following-update: #bd4938;
  --following-unlock-bg: #fff6e1;
  --following-unlock-text: #75603b;
  --following-unlock-link: #b06b00;
  --following-alert-border: #edf0f3;
  --following-unread-bg: #fff8f5;
  padding: 10px 8px 24px;
  color: var(--following-ink);
}
.page-heading { display:flex; align-items:center; justify-content:space-between; margin-bottom:12px; }
.page-heading h4 { margin:0 0 3px; font-weight:700; }
.page-heading p { margin:0; color:var(--following-muted); font-size:12px; }
.follow-grid { display:grid; grid-template-columns:repeat(3,minmax(0,1fr)); gap:12px; }
.follow-card { border:1px solid var(--following-border); border-radius:9px; background:var(--following-card-bg); padding:12px; }
.follow-card header,.score-row,.card-footer-line { display:flex; align-items:center; justify-content:space-between; gap:9px; }
.follow-card header a { color:var(--following-ink); font-size:16px; font-weight:700; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.follow-card header button { border:0; background:none; color:var(--following-muted); font-size:11px; white-space:nowrap; }
.score-row { margin:11px 0 8px; color:var(--following-muted); font-size:11px; }
.score-row b { color:var(--following-ink); font-size:14px; }
.score-row .up b { color:var(--following-up); }
.score-row .down b { color:var(--following-down); }
.state-box { min-height:82px; padding:9px; border-radius:6px; background:var(--following-soft-bg); }
.state-box strong { color:var(--following-up); }
.state-box > span { float:right; color:var(--following-muted); font-size:11px; }
.state-box p { margin:6px 0 0; color:var(--following-state-text); font-size:12px; line-height:1.5; }
.state-box.muted { color:var(--following-muted); font-size:12px; }
.card-footer-line { margin-top:9px; color:var(--following-muted); font-size:11px; }
.update-dot { color:var(--following-update); }
.unlock-line { margin:9px -12px -12px; padding:8px 12px; background:var(--following-unlock-bg); color:var(--following-unlock-text); font-size:11px; }
.unlock-line button { float:right; border:0; background:none; color:var(--following-unlock-link); font-weight:700; }
.empty-state { display:flex; flex-direction:column; align-items:center; gap:8px; padding:55px; border:1px dashed var(--following-border); color:var(--following-muted); }
.alerts-section { margin-top:18px; }
.alerts-section h5 { font-weight:700; }
.alert-row { display:grid; grid-template-columns:85px 220px 1fr; gap:10px; padding:8px; border-top:1px solid var(--following-alert-border); color:var(--following-muted); font-size:12px; }
.alert-row p { margin:0; }
.alert-row.unread { background:var(--following-unread-bg); }
@media(max-width:1000px){.follow-grid{grid-template-columns:repeat(2,minmax(0,1fr));}}@media(max-width:650px){.follow-grid{grid-template-columns:1fr}.alert-row{grid-template-columns:75px 1fr}.alert-row p{grid-column:1/-1}}
</style>

<style>
body.theme-dark .following-page {
  --following-border: #3b4554;
  --following-card-bg: #243247;
  --following-soft-bg: #2b3442;
  --following-state-text: #c6ceda;
  --following-up: #e88978;
  --following-down: #78c89a;
  --following-update: #e88978;
  --following-unlock-bg: #3b3428;
  --following-unlock-text: #d8bb77;
  --following-unlock-link: #e7bd6d;
  --following-alert-border: #334155;
  --following-unread-bg: #2d3644;
}
</style>
